import React, { useState, useEffect } from 'react'
import Lightbox from './Lightbox'
import { assetPath, imageUrl } from '../lib/images'

export default function GallerySection({ galleryRef, customTitle, customDescription }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [page, setPage] = useState(0)

  useEffect(() => {
    let isMounted = true
    setLoading(true)

    fetch('/_api/public/content/galleries')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente galleri')
        return res.json()
      })
      .then((data) => {
        if (!isMounted) return
        if (Array.isArray(data) && data.length > 0) {
          let matched = null
          if (galleryRef) {
            matched = data.find((g) => g.ref_slug === galleryRef || g.slug === galleryRef)
          }
          if (!matched) {
            matched = data.find((g) => g.ref_slug === 'faellesskab') || data[0]
          }

          const baseImgs = Array.isArray(matched?.images) ? matched.images : []
          const fallbackImgs = [
            '/_assets/dsc00737_b28d969a.jpg',
            '/_assets/dsc00815_f86d9057.jpg',
            '/_assets/dsc00901_0b4af1e4.jpg',
            '/_assets/dsc00981_2e69773d.jpg',
            '/_assets/dsc00991-1_bd637d45.jpg',
            '/_assets/dsc00998-1_20e0f98a.jpg',
            '/_assets/dsc01007_be359ec3.jpg',
            '/_assets/dsc01025_c49f68ab.jpg',
            '/_assets/dsc01030_e6621df6.jpg',
            '/_assets/dsc01047_578d4989.jpg',
            '/_assets/dsc01094_7872ed6d.jpg',
            '/_assets/dsc01126-0_1c1545bb.jpg',
            '/_assets/dsc00973_1268c8a9.jpg',
            '/_assets/dsc01164_b0f51fa9.jpg',
            '/_assets/dsc01037_cec92b3d.jpg',
            '/_assets/dsc00755_e31a980f.jpg',
            '/_assets/dsc00965_27a565d8.jpg',
            '/_assets/dsc01421-0_e9196308.jpg',
            '/_assets/dsc00976_f5bc3924.jpg',
            '/_assets/dsc01109_3b5b4903.jpg',
          ]
          const combined = Array.from(new Set([...baseImgs, ...fallbackImgs]))
          setGallery({ ...matched, images: combined })
        }
      })
      .catch((err) => {
        console.error('Gallery fetch error:', err)
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [galleryRef])

  if (loading) {
    return (
      <div className="py-8 text-center text-stone-400">
        <i className="fa-solid fa-circle-notch fa-spin text-xl text-[#007A53] mb-2" aria-hidden="true" />
        <p className="text-xs font-bold">Indlæser fotogalleri...</p>
      </div>
    )
  }

  if (!gallery || !Array.isArray(gallery.images) || gallery.images.length === 0) {
    return null
  }

  const images = (gallery.images || []).map((img) => assetPath(img))
  const title = customTitle || gallery.title || 'Billeder fra hverdagen'
  const totalPages = Math.ceil(images.length / 6)
  const currentImages = images.slice(page * 6, page * 6 + 6)

  return (
    <section className="my-16 sm:my-20" aria-label="Billedgalleri">
      <div className="border-t border-[#DCEEF2] pt-10 sm:pt-14">
        {/* Header med titel og navigationspile */}
        <div className="mb-8 sm:mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="font-['Shadows_Into_Light'] text-2xl text-[#007A53] block mb-1">
              {customDescription || 'Glimt af livet på skolen'}
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-2xl sm:text-3xl lg:text-4xl font-black text-[#36454F] tracking-tight">
              {title}
            </h2>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                disabled={page === 0}
                className="w-10 h-10 rounded-full bg-[#DCEEF2] text-[#2F6F73] hover:bg-[#007A53] hover:text-white flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer shadow-sm hover:scale-105"
                aria-label="Forrige billeder"
              >
                <i className="fa-solid fa-chevron-left text-xs" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                disabled={page >= totalPages - 1}
                className="w-10 h-10 rounded-full bg-[#DCEEF2] text-[#2F6F73] hover:bg-[#007A53] hover:text-white flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer shadow-sm hover:scale-105"
                aria-label="Næste billeder"
              >
                <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {/* Galleridesign: Store organiske billeder med varierende kurver */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 items-start">
          {currentImages.map((imgUrl, idx) => {
            const actualIndex = page * 6 + idx

            const config = [
              {
                height: 'h-48 sm:h-60 md:h-72',
                radius: 'rounded-organic',
                rotate: '-rotate-1 hover:rotate-0',
              },
              {
                height: 'h-48 sm:h-60 md:h-72',
                radius: 'rounded-organic',
                rotate: 'rotate-1 hover:rotate-0',
              },
              {
                height: 'h-48 sm:h-60 md:h-72',
                radius: 'rounded-organic',
                rotate: '-rotate-1 hover:rotate-0',
              },
              {
                height: 'h-48 sm:h-60 md:h-72',
                radius: 'rounded-organic',
                rotate: 'rotate-1 hover:rotate-0',
              },
              {
                height: 'h-48 sm:h-60 md:h-72',
                radius: 'rounded-organic',
                rotate: '-rotate-1 hover:rotate-0',
              },
              {
                height: 'h-48 sm:h-60 md:h-72',
                radius: 'rounded-organic',
                rotate: 'rotate-1 hover:rotate-0',
              },
            ][idx] || {
              height: 'h-48 sm:h-60 md:h-72',
              radius: 'rounded-3xl',
              rotate: '',
            }

            return (
              <button
                key={actualIndex}
                type="button"
                onClick={() => setLightboxIndex(actualIndex)}
                className={`group relative w-full ${config.height} ${config.radius} ${config.rotate} overflow-hidden bg-[#DCEEF2]/40 border-3 border-white shadow-md hover:shadow-2xl transition-all duration-500 ease-out focus:outline-none cursor-pointer text-left block transform-gpu hover:scale-103`}
                aria-label={`Åbn billede ${actualIndex + 1} i fuld skærm`}
              >
                <img
                  src={imageUrl(imgUrl, 'width=800,quality=80,format=auto')}
                  alt={`${title} - billede ${actualIndex + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-[#007A53]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none flex items-center justify-center">
                  <span className="w-10 h-10 rounded-full bg-white text-[#007A53] flex items-center justify-center shadow-md">
                    <i className="fa-solid fa-expand text-sm" />
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </section>
  )
}
